import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { Upload, Link as LinkIcon, Loader2, Image as ImageIcon } from 'lucide-react';
import { useAuthStore } from '../../../../store/authStore';
import axios from 'axios';
import ImageGallery from './ImageGallery';

// Fallback igual que lib/axios: sin REACT_APP_BACKEND_URL (dev server) apunta al
// backend local; en prod la env trae la URL real. Sin esto quedaba
// "undefined/api/images/upload" -> 404.
const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const ImageUploadDialog = ({ open, onOpenChange, onInsertImage }) => {
  const { token } = useAuthStore();
  const [activeTab, setActiveTab] = useState('upload');
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showGallery, setShowGallery] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato de imagen no soportado. Usa JPG, PNG, GIF, WebP o SVG.');
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('La imagen es demasiado grande. Máximo 10MB.');
      return;
    }

    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecciona una imagen primero');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${API}/images/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // The canonical /assets path is what we store (gallery) and insert into
      // the document; the signed URL (response.data.url) is only for direct <img>.
      const canonicalPath = response.data.path || response.data.url;
      const altText = imageAlt;

      // Save canonical path to gallery
      await saveToGallery(canonicalPath, altText);

      // Close dialog first to remove aria-hidden
      handleClose();

      // Then insert image after a small delay
      setTimeout(() => {
        onInsertImage(canonicalPath, altText);
        toast.success('Imagen insertada');
      }, 150);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleInsertFromUrl = async () => {
    if (!imageUrl.trim()) {
      toast.error('Ingresa una URL de imagen');
      return;
    }

    // Basic URL validation
    try {
      new URL(imageUrl);
    } catch {
      toast.error('URL inválida');
      return;
    }

    const url = imageUrl;
    const altText = imageAlt;

    // Save to gallery (badge must say URL, not upload)
    await saveToGallery(url, altText, 'url');

    // Close dialog first to remove aria-hidden
    handleClose();

    // Then insert image after a small delay
    setTimeout(() => {
      onInsertImage(url, altText);
      toast.success('Imagen insertada');
    }, 150);
  };

  const handleClose = () => {
    setImageUrl('');
    setImageAlt('');
    setSelectedFile(null);
    setPreviewUrl(null);
    setActiveTab('upload');
    onOpenChange(false);
  };

  const handleGallerySelect = (url, altText) => {
    onInsertImage(url, altText);
    setShowGallery(false);
    toast.success('Imagen insertada desde galería');
  };

  // Save uploaded image to gallery
  const saveToGallery = async (url, altText, source = 'upload') => {
    try {
      await axios.post(
        `${API}/images/gallery`,
        {
          url: url,
          alt_text: altText || '',
          source,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.error('Error saving to gallery:', error);
      // Don't show error to user, it's not critical
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="glass border-0 w-[90vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="image-upload-dialog">
        <DialogHeader>
          <DialogTitle>Insertar Imagen</DialogTitle>
          <DialogDescription>
            Sube una imagen desde tu computadora o inserta desde una URL.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="upload">
              <Upload className="w-4 h-4 mr-2" />
              Subir Archivo
            </TabsTrigger>
            <TabsTrigger value="url">
              <LinkIcon className="w-4 h-4 mr-2" />
              Desde URL
            </TabsTrigger>
            <TabsTrigger value="gallery">
              <ImageIcon className="w-4 h-4 mr-2" />
              Galería
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file-upload">Seleccionar Imagen</Label>
              <Input
                id="file-upload"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Formatos: JPG, PNG, GIF, WebP, SVG (máx. 10MB)
              </p>
            </div>

            {previewUrl && (
              <div className="space-y-2">
                <Label>Vista Previa</Label>
                <div className="border rounded-lg p-4 bg-white/50 dark:bg-slate-800/50 flex justify-center">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-h-64 object-contain"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="alt-text-upload">Texto Alternativo (opcional)</Label>
              <Input
                id="alt-text-upload"
                placeholder="Descripción de la imagen"
                value={imageAlt}
                onChange={(e) => setImageAlt(e.target.value)}
                disabled={uploading}
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={uploading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpload}
                className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
                disabled={!selectedFile || uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  'Insertar Imagen'
                )}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="url" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="image-url">URL de la Imagen</Label>
              <Input
                id="image-url"
                type="url"
                placeholder="https://ejemplo.com/imagen.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="alt-text-url">Texto Alternativo (opcional)</Label>
              <Input
                id="alt-text-url"
                placeholder="Descripción de la imagen"
                value={imageAlt}
                onChange={(e) => setImageAlt(e.target.value)}
              />
            </div>

            {imageUrl && (
              <div className="space-y-2">
                <Label>Vista Previa</Label>
                <div className="border rounded-lg p-4 bg-white/50 dark:bg-slate-800/50 flex justify-center">
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="max-h-64 object-contain"
                    onError={() => toast.error('No se pudo cargar la imagen desde la URL')}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleInsertFromUrl}
                className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
                disabled={!imageUrl.trim()}
              >
                Insertar Imagen
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="gallery" className="space-y-4">
            <div className="text-center py-8">
              <ImageIcon className="w-16 h-16 mx-auto mb-4 text-blue-500 opacity-50" />
              <p className="mb-4">Abre la galería completa para ver y seleccionar tus imágenes</p>
              <Button
                onClick={() => {
                  setShowGallery(true);
                  setActiveTab('upload'); // Reset to upload tab when opening gallery
                }}
                className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Abrir Galería
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>

    {/* Image Gallery Dialog */}
    <ImageGallery
      open={showGallery}
      onOpenChange={setShowGallery}
      onSelectImage={handleGallerySelect}
    />
    </>
  );
};

export default ImageUploadDialog;
